import React from 'react'
import LinesEllipsis from 'react-lines-ellipsis'
import Link from 'next/link'
import Image from 'next/image'
import { formatDate } from 'src/utils/time/moment.helper'
import Router from 'next/router'
import styles from './styles.module.scss'
import { SITENAME } from 'src/consts'
import { intl } from 'src/i18n'

const isPreviousMonth = date => {
  const [year, month] = date.split('/')
  const today = new Date()
  if (parseInt(year, 10) < today.getFullYear()) return true
  if (parseInt(month, 10) < today.getMonth() + 1) return true
  return false
}

const dateFormat = 'LLLL dd, yyyy'
const monthFormat = 'LLLL yyyy'

type Props = {
  eventsGroupByMonth?: { [key: string]: any[] }
}

export const EventsList = ({ eventsGroupByMonth }: Props) => {
  const today = new Date()
  const filtered = Object.entries(eventsGroupByMonth || {}).reduce(
    (acc, [date, events]) => {
      if (isPreviousMonth(date)) return acc
      const currentEvents = events.filter(
        event => new Date(event.end_date).getTime() > today.getTime()
      )
      if (!currentEvents.length) return acc
      acc.push([date, currentEvents])
      return acc
    },
    []
  )

  return (
    <div className={styles.events}>
      <h1 className={styles.header}>
        {intl.t('events.components.eventsList.header')}
      </h1>
      <div>
        {filtered.map(([date, events]) => (
          <div key={date}>
            <div className={styles.month}>
              {formatDate(new Date(`${date}/2`), monthFormat)}
            </div>
            <div>
              {events.map(
                ({
                  nameId,
                  title,
                  description,
                  start_date,
                  end_date,
                  cityName,
                  countryName,
                  image,
                  slug,
                }) => {
                  const descriptionText = description
                    .replace(/<\s*[a-z][^>]*>|<\s*[/]*[a-z][^>]*>/g, '')
                    .replace(/&nbsp;|&amp;/g, ' ')
                    .replace(/(\.)(\w)/g, '$1 $2')
                    .replace(/\r?\n|\r/g, ' ')

                  const startDate = formatDate(start_date, dateFormat)
                  const endDate = formatDate(end_date, dateFormat)
                  const eventLocation =
                    cityName.length && countryName.length
                      ? `${cityName}, ${countryName}`
                      : 'Online'

                  return (
                    <div className={styles.event} key={nameId}>
                      <div
                        className={styles.imageContainer}
                        onClick={() => Router.push(`/events/${slug || nameId}`)}
                      >
                        {image ? (
                          <Image
                            layout='fill'
                            alt={`${title} in the crypto calendar by Coin360: Blockchain conferences, Cryptocurrency forums, Summits and Other events`}
                            src={image}
                          />
                        ) : (
                          <div className={styles.emptyImage}>
                            <div className={styles.imageHead}>
                              <div className={styles.imageDate}>
                                {startDate} - {endDate}
                              </div>
                              <div className={styles.imageTittle}>{title}</div>
                            </div>
                            <div className={styles.imagePlace}>
                              {eventLocation}
                            </div>
                          </div>
                        )}
                      </div>
                      <div className={styles.eventsInfoContainer}>
                        <Link href={`/events/${slug || nameId}`} legacyBehavior>
                          <a className={styles.title}>{title}</a>
                        </Link>
                        <div className={styles.subtitle}>
                          {startDate} {end_date && ` - ${endDate}`} •{' '}
                          {eventLocation}
                        </div>
                        <LinesEllipsis
                          className={styles.description}
                          style={{ whiteSpace: 'pre-wrap' }}
                          text={descriptionText}
                          maxLine={3}
                          ellipsis='...'
                          trimRight
                          basedOn='letters'
                        />
                      </div>
                    </div>
                  )
                }
              )}
            </div>
          </div>
        ))}
      </div>
      <div className={styles.request}>
        {intl.t('events.components.eventsList.question')}
        <a href={`mailto:contact@${SITENAME}`}>contact@{SITENAME}</a>
        {intl.t('events.components.eventsList.question2')}
      </div>
    </div>
  )
}
